import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/_services/auth/auth.service';
import { LocalStorageService } from 'src/app/_services/localStorage/local-storage.service';
import { LoteService } from 'src/app/_services/lote/lote.service';
import { ExportOutput, ExportOutputType } from 'src/app/interfaces/exportationBatches.interfaces';
import { LocationInterface, LocationWithFilter } from 'src/app/interfaces/lotes.interfaces';
import { TonsPerYard } from 'src/app/interfaces/tons-per-yard.interfaces';
import { TonsPerYardService } from 'src/app/_services/tons-per-yard/tons-per-yard.service';
import { outputToAnotherPatio } from '../../utils/validators/outputToAnotherPatio';
import { Router } from '@angular/router';
import { LoteCabotajeService } from 'src/app/_services/loteCabotaje/lote-cabotaje.service';
import { BlDetails } from 'src/app/interfaces/bl.interfaces';

/**
 * Componente de formulario para registrar o actualizar la salida de un lote
 * hacia otro patio, soportando los flujos de importación, exportación y
 * cabotaje, con validación de tonelaje/piezas disponibles y navegación posterior.
 */
@Component({
  selector: 'app-output-form-to-another-patio',
  templateUrl: './output-form-to-another-patio.component.html',
  styles: [
  ]
})
export class OutputFormToAnotherPatioComponent implements OnInit, OnChanges {
  @Input() movimiento: string = 'crear';
  @Input() containerStyles = {
    'grid-template-columns': 'repeat(4, 1fr)'
  };
  @Input() outputDetail: ExportOutput[] = [];
  @Input() outputTypes: ExportOutputType[] = [];
  @Input() locations: LocationInterface[] = [];
  @Input() amountsPerYardList: TonsPerYard[] = [];
  @Input() blDetails: BlDetails = {
    operation_number:     0,
    boats_name:           '',
    operation_type_number:0,
    bl_tonnage:           0,
    customer_name:        null,
    date_time_capture:    '',
    dock_name:            '',
    port_origin_name:     '',
    start_operations:     '',
    end_operations:       '',
    boat_nationality:     '',
    destination_port:     '',
    operation_id:         0,
    docking_date:         '',
    undocking_date:       '',
    bl_master:            '',
    notice_of_readiness:  '',
  };
  @Output() onRegresar = new EventEmitter<boolean>();
  @Output() onTipoSalida = new EventEmitter<string>();
  locationsFiltered: LocationWithFilter[] = [];
  destinyLocation: LocationWithFilter[] = [];
  fullName = this.localStorageSvc.getFullName();
  isCheckerNameDisabled = this.fullName ? true : false;
  outputForm: FormGroup = this.fb.group({
    output_type_option_id: [],
    precinct_key: [201, [Validators.required]],
    total_tonnage: [{value: '0', disabled: true}],
    total_pieces: [{value: '0', disabled: true}],
    departure_date: [this.getDate(), [Validators.required]],
    observations: [],
    checker_name: [{value: this.fullName, disabled: this.isCheckerNameDisabled}, [Validators.required]],
    location_id: ['', [Validators.required]],
    location_tonnage: [{value: null, disabled: true}],
    location_pieces: [{value: null, disabled: true}],
    location_list: this.fb.array([this.createLocationGroup(true)])
  });
  outputType: FormControl = new FormControl('5');
  output!: any;
  isExport: boolean = this.router.url.includes('exportation');
  isCabotage: boolean = this.router.url.includes('cabotage');
  isImport: boolean = this.router.url.includes('importation');
  batchValue: number = 0;
  shipId: number = 0;

  // Loaders and Overlays
  overlay: boolean = false;
  overlayMessage: string = '';
  overlayIcon: string = '';
  overlayButtons: boolean = false;
  loading: boolean = false;
  overlayError: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private batchSvc: LoteService,
    private cabotageBatchSvc: LoteCabotajeService,
    private amountsPerYardSvc: TonsPerYardService,
    private authService: AuthService,
    private localStorageSvc: LocalStorageService,
  ) { }

  get locationArray() {
    return this.outputForm.get('location_list') as FormArray;
  }

  /**
   * Filtra las ubicaciones disponibles según el movimiento y las cantidades por patio cuando cambian las entradas.
   * @param changes - Cambios detectados en las propiedades de entrada del componente.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locations']) {
      this.destinyLocation = [...this.locations.map( location => ({
        ...location,
        disabled: false
      }))];
      // Id´s de las ubicaciones con tonelaje/piezas
      const idList: number[] = [];
      this.amountsPerYardList.forEach(location => {
        idList.push(location.id);
      });
      // Filtrar ubicaciones con tonelaje/piezas
      if (this.movimiento === 'crear') {
        this.locationsFiltered = [...this.destinyLocation.filter( location =>
          idList.includes(location.id)
        )];
      } else if (this.movimiento === 'actualizar') {
        // Id´s de las ubicaciones de la salida
        const outputLocationIdList: number[] = [];
        this.outputDetail[0].location_list.forEach(location => {
          outputLocationIdList.push(location.location_id);
        });
        this.locationsFiltered = [...this.destinyLocation.filter( location =>
          location.id === this.outputDetail[0].location_id || (idList.includes(location.id) && !outputLocationIdList.includes(location.id))
        )];
      } else {
        this.locationsFiltered = [...this.destinyLocation];
      }
    }
  }

  ngOnInit(): void {
    const parts = this.router.url.split("/");
    this.batchValue = Number(parts[parts.length - 1]);
    if (this.isImport) {
      this.shipId = Number(parts[2]);
    }

    if (this.movimiento !== 'crear') {
      // La cantidad de items dentro del array -1 porque ya esta creado
      const locationListLength = this.outputDetail[0].location_list.length - 1;
      for (let index = 0; index < locationListLength; index++) {
        this.addLocation();
      }
      this.outputForm.reset({
        ...this.outputDetail[0]
      });
      this.changeLocation();
      this.setTotals();

      // Deshabilitar la ubicacion cuando se actualiza
      this.locationArray.value.forEach((location: any, i: number) => {
        this.outputForm.get(`location_list.${i}.location_id`)!.disable();
      });

      // Formatear el formato de la fecha
      const departure_date = this.outputDetail[0].departure_date;
      const dateWithoutHour = departure_date.split(' ')[0];
      this.outputForm.get('departure_date')?.setValue(dateWithoutHour);

      // Agregar el array en caso de que se actualice sin realizar ningun cambio
      this.amountsPerYardSvc.locationsList = [
        ...this.locationArray.value.map((location: any, i: number) => ({
          ...location,
          pieces: this.outputForm.get(`location_list.${i}.pieces`)!.value,
          location_id: this.outputForm.get(`location_list.${i}.location_id`)!.value,
        }))
      ];
      this.outputForm.get('location_id')?.disable();
      this.locationArray.disable();
      this.outputType.disable();
    }

    if (this.movimiento === 'detalle') this.outputForm.disable();

    this.locationArray.valueChanges.subscribe( _ => {
      this.setTotals();
      this.amountsPerYardSvc.locationsList = [
        ...this.locationArray.value.map((location: any, i: number) => ({
          ...location,
          pieces: this.outputForm.get(`location_list.${i}.pieces`)!.value,
          location_id: this.outputForm.get(`location_list.${i}.location_id`)!.value,
        }))
      ];
    });

    this.outputType.valueChanges.subscribe( ouputTypeId => {
      this.onTipoSalida.emit(ouputTypeId);
    });
  }

  /**
   * Valida el formulario y dispara la creación o actualización de la salida según el movimiento actual.
   */
  saveOutput() {
    if (this.outputForm.invalid) {
      this.outputForm.markAllAsTouched();
      this.handleOverlay(
        'Favor de verificar la información',
        'bi bi-exclamation-circle-fill',
        false,
        2000
      );
      return;
    }
    this.output = {
      ...this.output,
      ...this.outputForm.value,
      checker_name: this.outputForm.get('checker_name')?.value,
      total_tonnage: Number(this.outputForm.get('total_tonnage')?.value),
      location_id: Number(this.outputForm.get('location_id')?.value),
      total_pieces: this.outputForm.get('total_pieces')?.value ? Number(this.outputForm.get('total_pieces')?.value) : 0,
    };
    if (this.movimiento === 'crear') {
      this.handleCreateOutput();
    } else if (this.movimiento === 'actualizar') {
      this.handleUpdateOutput();
    }
  }

  /**
   * Valida que el tonelaje y las piezas a transferir no excedan lo disponible y crea la salida según el módulo.
   */
  handleCreateOutput() {
    const locationTonnage = Number(this.outputForm.get('location_tonnage')?.value);
    const locationPieces = Number(this.outputForm.get('location_pieces')?.value);
    let isAmountsValid = true;
    let message = '';
    if (this.output.total_tonnage > locationTonnage) {
      message = 'El tonelaje a transferir es mayor al disponible en la ubicación';
      isAmountsValid = false;
    }
    if (this.output.total_pieces > locationPieces) {
      message = 'Las piezas a transferir son mayores a las disponibles en la ubicación';
      isAmountsValid = false;
    }
    if (!isAmountsValid) {
      this.handleOverlay(
        message,
        'bi bi-exclamation-circle-fill',
        false,
        3000
      );
      return;
    }
    if (this.isExport) {
      this.createExportOutput();
    } else if (this.isCabotage) {
      this.createCabotageOutput();
    } else if (this.isImport) {
      this.createImportOutput();
    }
  }

  /**
   * Actualiza la salida según el módulo activo (importación, exportación o cabotaje).
   */
  handleUpdateOutput() {
    if (this.isExport) {
      this.updateExportOutput();
    } else if (this.isCabotage) {
      this.updateCabotageOutput();
    } else if (this.isImport) {
      this.updateImportOutput();
    }
  }

  /**
   * Registra una salida de importación y refresca la información del lote, avisando si se excede el 2% de tonelaje.
   */
  createImportOutput() {
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.batch_id = this.batchValue;
    this.output.total = this.output.total_tonnage;
    delete this.output.total_tonnage;
    this.loading = true;
    this.batchSvc.createSalida(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.batchSvc.getLoteId(this.batchValue)
        .subscribe({
          next: batch => {
            this.batchSvc.loteInfo = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue agregada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  this.router.navigate([`/ship-list/${this.shipId}/importation/batch-movement/output-list`, this.batchValue])
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              this.router.navigate([`/ship-list/${this.shipId}/importation/batch-movement/output-list`, this.batchValue])
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Registra una salida de exportación y refresca la información del lote, avisando si se excede el 2% de tonelaje.
   */
  createExportOutput() {
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.export_batch_id = this.batchValue;
    this.loading = true;
    this.batchSvc.createExportOutput(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.batchSvc.getExportationBatch(this.batchValue)
        .subscribe({
          next: batch => {
            this.batchSvc.loteInfoExport = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue agregada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  this.router.navigate([`/exportation/batch-movement/output-list`, this.batchValue])
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              this.router.navigate([`/exportation/batch-movement/output-list`, this.batchValue])
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Registra una salida de cabotaje (validando anexos del lote) y refresca su información, avisando si se excede el 2% de tonelaje.
   */
  createCabotageOutput() {
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.cabotage_batch_id = this.batchValue;
    const isExportCabotage = !this.router.url.includes('ship-list');
    if (!this.cabotageBatchSvc.loteInfoCabotage.keys_files?.length && isExportCabotage) {
      this.handleOverlay(
        'No se puede registrar salida porque el lote no tiene anexos',
        'bi bi-info-circle-fill',
        false,
        4000
      );
      return;
    }
    this.loading = true;
    this.cabotageBatchSvc.createCabotageOutput(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.cabotageBatchSvc.getCabotageBatch(this.batchValue)
        .subscribe({
          next: batch => {
            this.cabotageBatchSvc.loteInfoCabotage = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue agregada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  if (this.router.url.includes('/ship-list')) {
                    this.router.navigate([`/ship-list/${this.blDetails.operation_number}/cabotage/batch-movement/output-list`, this.batchValue]);
                  } else {
                    this.router.navigate([`/cabotage/batch-movement/output-list`, this.batchValue])
                  }
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              if (this.router.url.includes('/ship-list')) {
                this.router.navigate([`/ship-list/${this.blDetails.operation_number}/cabotage/batch-movement/output-list`, this.batchValue]);
              } else {
                this.router.navigate([`/cabotage/batch-movement/output-list`, this.batchValue])
              }
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Actualiza una salida de importación y refresca la información del lote, avisando si se excede el 2% de tonelaje.
   */
  updateImportOutput() {
    this.output.id = this.outputDetail[0].id;
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.batch_id = this.batchValue;
    this.output.total = this.output.total_tonnage;
    delete this.output.total_tonnage;
    this.loading = true;
    this.batchSvc.updateSalida(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.batchSvc.getLoteId(this.batchValue)
        .subscribe({
          next: batch => {
            this.batchSvc.loteInfo = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue actualizada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  this.regresar();
                  this.router.navigate([`/ship-list/${this.shipId}/importation/batch-movement/output-list`, this.batchValue])
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              this.regresar();
              this.router.navigate([`/ship-list/${this.shipId}/importation/batch-movement/output-list`, this.batchValue])
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Actualiza una salida de exportación y refresca la información del lote, avisando si se excede el 2% de tonelaje.
   */
  updateExportOutput() {
    this.output.id = this.outputDetail[0].id;
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.export_batch_id = this.batchValue;
    this.loading = true;
    this.batchSvc.updateExportOutput(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.batchSvc.getExportationBatch(this.batchValue)
        .subscribe({
          next: batch => {
            this.batchSvc.loteInfoExport = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue actualizada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  this.regresar();
                  this.router.navigate([`/exportation/batch-movement/output-list`, this.batchValue])
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              this.regresar();
              this.router.navigate([`/exportation/batch-movement/output-list`, this.batchValue])
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Actualiza una salida de cabotaje y refresca la información del lote, avisando si se excede el 2% de tonelaje.
   */
  updateCabotageOutput() {
    this.output.id = this.outputDetail[0].id;
    this.output.output_type_option_id = Number(this.outputType.value);
    this.output.cabotage_batch_id = this.batchValue;
    this.loading = true;
    this.cabotageBatchSvc.updateCabotageOutput(this.output)
    .subscribe({
      next: response => {
        if (response.error) {
          this.loading = false;
          const message = response.message.includes('tonnage')
            ? 'La suma de toneladas es mayor a las toneladas de entrada'
            : response.message.includes('pieces')
            ? 'La suma de piezas es mayor a las piezas totales de entrada'
            : 'Lo sentimos no fue posible registrar la salida, inténtelo más tarde';
          this.handleOverlay(
            message,
            'bi bi-exclamation-circle-fill',
            false,
            4000
          );
          return;
        }
        this.cabotageBatchSvc.getCabotageBatch(this.batchValue)
        .subscribe({
          next: batch => {
            this.cabotageBatchSvc.loteInfoCabotage = batch[0];
            const inputTons = batch[0].input_tons;
            const outputTons = Number(batch[0].output_tons?.toFixed(3));
            const twoPercent = inputTons ? inputTons * 0.02 : 0;
            let inputTons_twoPercent = Number(inputTons) + Number(twoPercent);
            inputTons_twoPercent = Number(inputTons_twoPercent.toFixed(3));
            this.loading = false;
            this.overlayMessage = 'La salida fue actualizada de manera correcta';
            this.overlayIcon = 'bi bi-check2';
            this.overlayButtons = false;
            this.overlay = true;
            if (outputTons > inputTons_twoPercent) {
              setTimeout(() => {
                this.overlayMessage = 'Tonelaje ha exedido mas del 2%';
                this.overlayIcon = 'bi bi-exclamation-circle-fill';
                setTimeout(() => {
                  this.overlay = false;
                  this.regresar();
                }, 3000);
              }, 2000);
              return;
            }
            setTimeout(() => {
              this.overlay = false;
              this.regresar();
            }, 2000);
          },
          error: (e) => {
            this.loading = false;
            this.handleOverlay(
              'Error en el servidor al actualizar el lote, inténtelo más tarde',
              'bi bi-exclamation-circle-fill',
              false,
              3000
            );
          },
        });
      },
      error: (e) => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al crear el registro, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      },
    });
  }

  /**
   * Elimina la ubicación en el índice indicado, gestionando el borrado en servidor durante la actualización.
   * @param index - Índice de la ubicación a eliminar dentro del arreglo del formulario.
   */
  deleteLocation(index: number) {
    const yardId = Number(this.outputForm.get(`location_list.${index}.id`)?.value);
    if (this.movimiento === 'actualizar' && yardId) {
      if (!this.canDelete()) return;
      this.deleteYard(yardId, index);
      return;
    }
    if (this.locationArray.length >= 2) this.locationArray.removeAt(index);
  }

  /**
   * Indica si hay más de una ubicación con identificador, condición para permitir su eliminación.
   * @returns `true` si se puede eliminar una ubicación; `false` en caso contrario.
   */
  canDelete() {
    let yardsWithId = 0;
    this.locationArray.value.forEach((location: any) => {
      if (location.id) yardsWithId++;
    });
    return yardsWithId > 1 ? true : false;
  }

  /**
   * Elimina una ubicación (patio) en el servidor y la quita del formulario al confirmarse.
   * @param id - Identificador del patio a eliminar.
   * @param index - Índice del patio dentro del arreglo del formulario.
   */
  deleteYard( id: number, index: number ) {
    this.loading = true;
    this.amountsPerYardSvc.deleteYard(id).subscribe({
      next: (response) => {
        this.loading = false;
        if (response.error) {
          this.handleOverlay(
            'Lo sentimos no fue posible eliminar la ubicación, inténtelo más tarde',
            'bi bi-exclamation-circle-fill',
            false,
            2000
          );
          return;
        }
        this.locationArray.removeAt(index);
      }, error: () => {
        this.loading = false;
        this.handleOverlay(
          'Error en el servidor al eliminar la ubicación, inténtelo más tarde',
          'bi bi-exclamation-circle-fill',
          false,
          3000
        );
      }
    });
  }

  /**
   * Calcula los totales de tonelaje y piezas a transferir a partir de las ubicaciones del formulario.
   */
  setTotals() {
    const tonsToTransfer = this.locationArray.value.reduce( (total: number, location: any) => {
      if (location.tonnage && Number(location.tonnage) > 0) return Number(location.tonnage) + total
      return total
    }, 0);
    const piecesToTransfer = this.locationArray.value.reduce( (total: number, location: any, i: number) => {
      const pieces = this.outputForm.get(`location_list.${i}.pieces`)!.value;
      if (pieces && Number(pieces) > 0) return Number(pieces) + total
      return total
    }, 0);
    this.outputForm.get('total_tonnage')?.setValue(tonsToTransfer);
    this.outputForm.get('total_pieces')?.setValue(piecesToTransfer);
  }

  /**
   * Indica si un campo del formulario es inválido y ha sido tocado.
   * @param campo - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidField( campo: string): boolean | null {
    return this.outputForm.get(campo)!.errors &&
    this.outputForm.get(campo)!.touched;
  }

  /**
   * Indica si un campo de una ubicación del arreglo es inválido y ha sido tocado.
   * @param index - Índice de la ubicación dentro del arreglo del formulario.
   * @param fieldName - Nombre del campo a evaluar.
   * @returns `true` si el campo tiene errores y fue tocado; en caso contrario `null` o `false`.
   */
  isValidArray( index: number, fieldName: string ): boolean | null {
    return this.outputForm.get(`location_list.${index}.${fieldName}`)!.errors && this.outputForm.get(`location_list.${index}.${fieldName}`)!.touched
  }

  /**
   * Agrega un nuevo grupo de ubicación al formulario, solo cuando el movimiento es de creación.
   */
  addLocation() {
    if (this.movimiento !== 'crear') {
      return;
    }
    this.locationArray.push(this.createLocationGroup());
  }

  /**
   * Crea un grupo de formulario con los campos y validadores de una ubicación.
   * @param isFirst - `true` cuando se trata de la primera ubicación; ajusta el valor inicial de piezas.
   * @returns El `FormGroup` que representa una ubicación.
   */
  createLocationGroup(isFirst = false): FormGroup {
    const pieces = isFirst ? 0 : Number(this.outputForm.get('total_pieces')?.value);
    return this.fb.group({
      id: [],
      location_id: ['', [Validators.required]],
      tonnage: [, [Validators.required, Validators.pattern(/^\d+(\.\d{1,3})?$/)]],
      pieces: [{ value: 0, disabled: pieces === 0 }, [Validators.required]]
    });
  }

  /**
   * Carga el tonelaje y las piezas disponibles de la ubicación origen seleccionada y habilita o deshabilita el campo de piezas.
   */
  changeLocation() {
    const locationId = Number(this.outputForm.get(`location_id`)!.value);
    const amountLocation = this.amountsPerYardList.find(yard => yard.id === locationId);
    const locationTonnage = amountLocation ? amountLocation.tonnage : 0;
    const locationPieces = amountLocation ? amountLocation.pieces : 0;
    this.outputForm.get(`location_tonnage`)!.setValue(locationTonnage);
    this.outputForm.get(`location_pieces`)!.setValue(locationPieces);
    // Habilitar o deshabilitar las piezas
    this.locationArray.value.forEach((location: any, i: number) => {
      if (Number(locationPieces) === 0) {
        this.outputForm.get(`location_list.${i}.pieces`)!.disable();
      } else {
        this.outputForm.get(`location_list.${i}.pieces`)!.enable();
      }
    });
  }

  /**
   * Emite el evento para regresar a la vista anterior.
   */
  regresar() {
    this.onRegresar.emit(true);
  }

  /**
   * Muestra un overlay con mensaje e ícono y lo oculta tras el tiempo indicado.
   * @param message - Mensaje a mostrar.
   * @param icon - Clase del ícono a mostrar.
   * @param buttons - `true` para mostrar botones de confirmación.
   * @param time - Tiempo en milisegundos antes de ocultar el overlay.
   */
  handleOverlay(message: string, icon: string, buttons: boolean, time: number) {
    this.overlayMessage = message;
    this.overlayIcon = icon;
    this.overlayButtons = buttons;
    this.overlay = true;
    setTimeout(() => {
      this.overlay = false;
    },time);
  }

  /**
   * Obtiene la fecha actual del sistema formateada como `AAAA-MM-DD`.
   * @returns La fecha actual en formato `AAAA-MM-DD`.
   */
  getDate(): string {
    const currentDate = new Date();
    const year = currentDate.getFullYear();
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getDate()).padStart(2, '0');
    const date = `${year}-${month}-${day}`;
    return date;
  }
}
